/* eslint-disable max-lines */
// deps
import React, { ReactElement, useState, memo, useCallback, useEffect, useMemo } from "react";
import cx from "classnames";

// internal-deps
import { CartItemWithUUID, FreeCashRemovalPopupVariant, ItemImagesOnCartXPVariants } from "@swiggy-private/cart-types";

// widgets
import { FoodItemInfoCondensed, FoodItemInfo } from "@cart-ui/widgets/food-item-info";

// helpers
import { isItemCustomizable, isMerchItem } from "@cart-ui/helpers/Item";
import { priceInRupee } from "@cart-ui/helpers/Price";

// components
import { FoodItemTypeFreebie } from "./types/FoodItemTypeFreebie";
import { FoodItemTypeInStock } from "./types/FoodItemTypeInStock";
import { FoodItemTypeOutOfStock } from "./types/FoodItemTypeOutOfStock";
import { CustomiseRepeatModal } from "./components/CustomiseRepeatModal";
import { FreeCashRemovalPopup } from "../free-cash-removal-popup";

//constants
import { TEXTS } from "@cart-ui/constants/index";
import { QTY_CHANGE_ACTION_TYPE, TEXT } from "./constants";

// test ids
import { CART_ITEMS_TEST_IDS } from "@swiggy-private/cart-test-ids";

// styles
import styles from "./styles/FoodItem.scss";
import { StarIcon } from "./StarIcon";
import { FreebieHeaderWrapper, FreebieHeaderText } from "./styles/foodItemsStyles";
import { isPopFlow } from "@swiggy-private/checkout-core";
import { isItemAvailable } from "@cart-ui/helpers/ItemOrderability";
import { FoodItemWidgetProps } from "./types/FoodItem";
import { isEmpty } from "@swiggy-private/js-utils";

function FoodItemWidget(props: FoodItemWidgetProps): ReactElement {
    const { disableActions = false, item, onUpdateItem } = props;
    const [showRepeatModal, setShowRepeatModal] = useState(false);
    const isPopCart = isPopFlow();
    const isMerchItemFlag = useMemo(() => isMerchItem(props.item), [props.item]);
    const [showFreeCashRemovalPopup, setShowFreeCashRemovalPopup] = useState(false);
    const [pendingFreeCashQtyDecItem, setPendingFreeCashQtyDecItem] = useState<CartItemWithUUID | null>(null);
    const [pendingFreeCashCustomise, setPendingFreeCashCustomise] = useState(false);

    // food item actions
    // When user clicks on customise text in item info
    const onCustomiseItem = React.useCallback(() => {
        props.analyticsEvent.onCustomiseUpdateItem(item.menu_item_id);
        if (!isEmpty(props.item.toing_item_attributes?.pc_free_cash_qty_dec_popup)) {
            props.setUpdating(item);
            setPendingFreeCashCustomise(true);
            setShowFreeCashRemovalPopup(true);
            return;
        }
        props.setUpdating(item);
        props.onCustomiseItem(props.item, false);
        props.setUpdating(null);
    }, [props, item]);

    // when user clicks i'll choose in bottom sheet in increment item flow
    const onCustomiseAddItem = React.useCallback(() => {
        props.analyticsEvent.onImpressionProgressCustomisation(item.menu_item_id);
        props.onCustomiseItem(props.item, true);
        setShowRepeatModal(false);
    }, [props, item]);

    const onRemoveItem = React.useCallback(async () => {
        props.analyticsEvent.onClickRemoveSingleOOSItem(item.menu_item_id, item.final_price);

        props.setUpdating(item);

        await onUpdateItem({ ...item, quantity: 0 });
        props.setUpdating(null);
    }, [item, onUpdateItem, props]);

    const getItemQty = useCallback(
        (action: QTY_CHANGE_ACTION_TYPE, qty?: number) => {
            switch (action) {
                case QTY_CHANGE_ACTION_TYPE.INCREMENT:
                    return props.item.quantity + 1;
                case QTY_CHANGE_ACTION_TYPE.DECREMENT:
                    return props.item.quantity - 1;
                case QTY_CHANGE_ACTION_TYPE.QTY_CHANGE:
                    return qty || 0;
            }
        },
        [props.item.quantity],
    );
    const getRequestCakeMessage = useCallback(
        // eslint-disable-next-line @typescript-eslint/naming-convention
        (action: QTY_CHANGE_ACTION_TYPE, qty: number, customer_request_messages?: string[]) => {
            const messages = customer_request_messages || [];
            switch (action) {
                case QTY_CHANGE_ACTION_TYPE.INCREMENT:
                    return qty > 1 ? messages?.slice(0, qty - 1) : [];
                case QTY_CHANGE_ACTION_TYPE.DECREMENT:
                    return messages?.slice(0, qty);
                case QTY_CHANGE_ACTION_TYPE.QTY_CHANGE:
                    return messages?.slice(0, qty);
            }
        },
        [],
    );
    const onQuantityChange = React.useCallback(
        async (action: QTY_CHANGE_ACTION_TYPE, qty: number) => {
            if (action === QTY_CHANGE_ACTION_TYPE.INCREMENT && props.item.disable_increase_qty) {
                props.onDisabledIncrementItemQtyClick();
                return;
            }

            const analyticsFn =
                action === QTY_CHANGE_ACTION_TYPE.INCREMENT
                    ? props.analyticsEvent.onClickItemIncrement
                    : props.analyticsEvent.onClickItemDecrement;
            analyticsFn(
                props.item.menu_item_id,
                action === QTY_CHANGE_ACTION_TYPE.INCREMENT ? props.item.quantity + 1 : props.item.quantity - 1,
                "cart-item",
                props.item.item_group_tag_id,
            );

            props.setUpdating(item);

            // if customisable item show customise window, unless it is a group cart
            if (
                !props.isGroupOrderFlow &&
                isItemCustomizable(props.item) &&
                action === QTY_CHANGE_ACTION_TYPE.INCREMENT
            ) {
                setShowRepeatModal(true);
                return;
            }
            /* eslint-disable @typescript-eslint/naming-convention */
            const updatedItem = {
                ...props.item,
                quantity: getItemQty(action, qty),
                // update cart on launch is not sanitizing.
                customer_request_messages: getRequestCakeMessage(
                    action,
                    getItemQty(action, qty) || 0,
                    props.item.customer_request_messages,
                ),
            };
            /* eslint-enable @typescript-eslint/naming-convention */
            if (
                action === QTY_CHANGE_ACTION_TYPE.DECREMENT &&
                !isEmpty(props.item.toing_item_attributes?.pc_free_cash_qty_dec_popup)
            ) {
                setPendingFreeCashQtyDecItem(updatedItem);
                setShowFreeCashRemovalPopup(true);
                return;
            }

            await onUpdateItem(updatedItem);
            props.setUpdating(null);
        },
        [props, item, getItemQty, getRequestCakeMessage, onUpdateItem],
    );

    const closeFreeCashRemovalPopup = useCallback(() => {
        setShowFreeCashRemovalPopup(false);
        setPendingFreeCashQtyDecItem(null);
        setPendingFreeCashCustomise(false);
        props.setUpdating(null);
    }, [props]);

    const onConfirmFreeCashQtyDec = useCallback(async () => {
        if (pendingFreeCashCustomise) {
            closeFreeCashRemovalPopup();
            props.setUpdating(item);
            props.onCustomiseItem(props.item, false);
            props.setUpdating(null);
            return;
        }
        if (isEmpty(pendingFreeCashQtyDecItem)) {
            return;
        }
        closeFreeCashRemovalPopup();
        await onUpdateItem(pendingFreeCashQtyDecItem);
        props.setUpdating(null);
    }, [closeFreeCashRemovalPopup, onUpdateItem, pendingFreeCashCustomise, pendingFreeCashQtyDecItem, props, item]);

    const onSecondaryFreeCashClick = useCallback(() => {
        closeFreeCashRemovalPopup();
    }, [closeFreeCashRemovalPopup]);

    const onPrimaryFreeCashClick = useCallback(() => {
        onConfirmFreeCashQtyDec();
    }, [onConfirmFreeCashQtyDec]);

    /* eslint-disable @typescript-eslint/naming-convention */
    const onRepeatItem = useCallback(async () => {
        const updatedItem = {
            ...props.item,
            quantity: props.item.quantity + 1,
            customer_request_messages: getRequestCakeMessage(
                QTY_CHANGE_ACTION_TYPE.INCREMENT,
                props.item.quantity + 1,
                props.item.customer_request_messages,
            ),
        };
        setShowRepeatModal(false);
        await onUpdateItem(updatedItem);
        props.setUpdating(null);
    }, [props, getRequestCakeMessage, onUpdateItem]);
    /* eslint-enable @typescript-eslint/naming-convention */

    const onCloseRepeatModal = useCallback(() => {
        setShowRepeatModal(false);
        props.setUpdating(null);
    }, [props]);

    const onItemImageLoad = useCallback(() => {
        props.analyticsEvent.onItemImageImpression(item.menu_item_id);
    }, [item.menu_item_id, props.analyticsEvent]);

    useEffect(() => {
        if (showRepeatModal) {
            props.analyticsEvent.onImpressionNormalCustomisation(item.menu_item_id);
        }
    }, [props.analyticsEvent, showRepeatModal, item]);

    // Logic for fetching food item type component
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const FoodItemTypeComponent = React.useCallback((): React.FunctionComponent<any> => {
        if (props.item.rewardType === "Freebie" && isItemAvailable(props.item)) {
            return FoodItemTypeFreebie;
        } else if (isItemAvailable(props.item)) {
            return FoodItemTypeInStock;
        } else {
            return FoodItemTypeOutOfStock;
        }
    }, [props.item])();

    const getFoodSymbolIconAccessibilityText = useCallback((isVeg: number) => {
        if (isVeg === 0) return TEXTS.NON_VEG_ITEM;
        else if (isVeg === 1) return TEXTS.VEG_ITEM;
        else return TEXTS.EGG_ITEM;
    }, []);

    const dataTestId = useMemo(() => {
        switch (FoodItemTypeComponent) {
            case FoodItemTypeFreebie:
                return CART_ITEMS_TEST_IDS.CART_ITEM_FREEBIE_ITEM;
            case FoodItemTypeInStock:
                return CART_ITEMS_TEST_IDS.CART_ITEM_IN_STOCK_ITEM;
            default:
                return CART_ITEMS_TEST_IDS.CART_ITEM_OOS_ITEM;
        }
    }, [FoodItemTypeComponent]);

    const FoodItemInfoComponent = useMemo(() => {
        if (!props.variant || props.variant === ItemImagesOnCartXPVariants.DEFAULT) return FoodItemInfo;
        return FoodItemInfoCondensed;
    }, [props.variant]);

    const Wrapper = props.isFreebieOptInItem && isPopCart ? "div" : React.Fragment;

    // Prepare wrapper props for freebie opt-in items
    const wrapperProps = useMemo(() => {
        if (!props.isFreebieOptInItem) {
            return {};
        }

        return {
            className: cx({
                [styles.containerPopOptIn]: props.isFreebieOptInItem,
                [styles.containerPopCouponGroupPresent]: props.isCouponGroup,
            }),
            "data-testid": CART_ITEMS_TEST_IDS.CART_ITEM_FREEBIE_WRAPPER,
        };
    }, [props.isFreebieOptInItem]);

    return (
        <Wrapper {...wrapperProps}>
            {props.isFreebieOptInItem && isPopCart && (
                <FreebieHeaderWrapper data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_FREEBIE_HEADER_TEXT}>
                    <StarIcon />
                    <FreebieHeaderText color="positive" variant="V2.body3.neutral.bold">
                        {TEXT.FREEITEM}
                    </FreebieHeaderText>
                    <StarIcon />
                </FreebieHeaderWrapper>
            )}
            <div
                className={cx(
                    {
                        [styles.container]: true,
                        [styles.containerOutOfStock]: !isItemAvailable(props.item),
                    },
                    props.className,
                )}
                data-testid={dataTestId}
            >
                <FoodItemTypeComponent
                    item={props.item}
                    disableActions={disableActions}
                    onQuantityChange={onQuantityChange}
                    removeItem={onRemoveItem}
                    updating={props.updating}
                    quantity={props.item.quantity}
                    freeQuantity={props.item.free_item_quantity}
                    showInputQtyField={props.showInputQtyField}
                    variant={props.variant}
                    onItemImageLoad={onItemImageLoad}
                    isHost={props.isHost}
                    isGroupOrderFlow={props.isGroupOrderFlow}
                    isCurrentItemFromSameUser={props.isCurrentItemFromSameUser}
                    isFreebieOptInItem={props.isFreebieOptInItem}
                    isPopCart={isPopCart}
                    onImpressionMagicItemsOfferLimit={props.analyticsEvent.onImpressionMagicItemsOfferLimit}
                >
                    <div
                        className={cx({
                            [styles.infoWrapper]: true,
                        })}
                    >
                        <FoodItemInfoComponent
                            isCustomisable={isItemCustomizable(props.item)}
                            onCustomise={onCustomiseItem}
                            addons={props.item.addons}
                            variants={props.item.variants}
                            isFreebie={props.item.rewardType === "Freebie"}
                            name={props.item.name}
                            className={styles.info}
                            quantity={props.item.quantity}
                            itemTypeText={getFoodSymbolIconAccessibilityText(props.item.isVeg)}
                            finalPrice={priceInRupee(props.item.final_price)}
                            isGroupOrderFlow={props.isGroupOrderFlow}
                            vegType={props.item.isVeg}
                            isMerchItem={isMerchItemFlag}
                        />
                    </div>
                </FoodItemTypeComponent>
                {showRepeatModal && (
                    <CustomiseRepeatModal
                        isVeg={props.item.isVeg}
                        name={props.item.name}
                        onCustomise={onCustomiseAddItem}
                        onRepeat={onRepeatItem}
                        price={props.item.final_price}
                        onClose={onCloseRepeatModal}
                    />
                )}
            </div>
            {showFreeCashRemovalPopup && (!isEmpty(pendingFreeCashQtyDecItem) || pendingFreeCashCustomise) && (
                <FreeCashRemovalPopup
                    variant={FreeCashRemovalPopupVariant.ITEM_REMOVAL}
                    closeModal={closeFreeCashRemovalPopup}
                    onSecondaryClick={onSecondaryFreeCashClick}
                    onPrimaryClick={onPrimaryFreeCashClick}
                    onImpression={props.analyticsEvent.onImpressionCartUpdateWarningD2C}
                    onClickAnalytics={props.analyticsEvent.onClickCartUpdateWarningD2C}
                />
            )}
        </Wrapper>
    );
}
export default memo(FoodItemWidget);
